import { IntlProvider } from 'react-intl';
import { useEffect, useState } from 'react';
import { RouterProvider } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { CssBaseline, ThemeProvider } from '@mui/material';
import {
  DataProvider,
  LocalizationProvider,
  SnackbarProvider,
  lightTheme,
  useStore,
} from '@onpointwarranty/claims-design-system';
import { observer } from 'mobx-react';
import jwtDecode from 'jwt-decode';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import GlobalErrorHandler from '@components/ErrorHandling/GlobalErrorHandler';
import getDomain from '@utils/GetDomain';
import apiClient from '@components/DataProvider/axios';
import defaultDataProvider from './components/DataProvider/default';
import router from './router/index';
import 'dayjs/locale/es';

async function loadLocaleData(locale = 'en') {
  let selectedLanguage = null;
  switch (locale) {
    case 'es':
      selectedLanguage = await import('@locales/es.json');
      break;
    default:
      selectedLanguage = await import('@locales/en.json');
      break;
  }
  return selectedLanguage;
}

function App() {
  const auth = useAuth();
  const store = useStore();
  const defaultLocale = 'en';

  const [messages, setMessages] = useState(null);
  const params = new URLSearchParams(window.location.search);
  // fetching token from url
  const accessToken = params.get('access_token');

  if (accessToken) {
    const decoded = jwtDecode(accessToken);
    auth.settings.userStore.set('user', JSON.stringify(decoded));
    auth.settings.userStore.set('token', accessToken);
  }

  const domain = getDomain('api');

  const getToken = () => {
    let token = '';
    /* eslint no-underscore-dangle: 0 */
    token = auth?.settings?.userStore?._store['oidc.token'];
    return token;
  };

  const axiosWrapper = apiClient(
    domain,
    auth?.user?.access_token || getToken(),
    store.domain.locale,
  );

  const dataProviders = {
    default: defaultDataProvider(axiosWrapper),
  };

  useEffect(() => {
    async function loadLocales(localeToLoad) {
      const localeMessages = await loadLocaleData(localeToLoad);
      if (localeMessages) {
        setMessages(localeMessages);
      }
    }

    loadLocales(store.domain.locale.get());
  }, [store.domain.locale.language]);

  const SnackbarProps = {
    anchorOrigin: { vertical: 'top', horizontal: 'center' },
    autoHideDuration: 5000,
  };

  return (
    <IntlProvider
      locale={store.domain.locale.get()}
      defaultLocale={defaultLocale}
      messages={messages}>
      <DataProvider dataProviders={dataProviders}>
        <ThemeProvider theme={lightTheme}>
          <GlobalErrorHandler>
            <CssBaseline />

            {messages && store.domain.locale.get() && (
              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale={store.domain.locale.get()}>
                <SnackbarProvider SnackbarProps={SnackbarProps}>
                  <RouterProvider router={router} />
                </SnackbarProvider>
              </LocalizationProvider>
            )}
          </GlobalErrorHandler>
        </ThemeProvider>
      </DataProvider>
    </IntlProvider>
  );
}

export default observer(App);
